import {
  CoverageReason,
  CoverageSummaryResponse,
  PupilEnforcementResponse
} from '@core/models/response/restriction.response';

/**
 * Coverage and enforcement wording (DIGI-1699, DIGI-1724), shared by the restriction cards, the
 * year group panel and the enforcement dialog so that one school reads one vocabulary.
 */

/**
 * Why a pupil could not have been restricted, in words a member of staff can act on. Each one
 * names something a family can fix, which is the point of splitting them out: "47 not confirmed"
 * tells a school nothing it can do anything about.
 */
export const COVERAGE_REASON_LABELS: Record<CoverageReason, string> = {
  NO_DEVICE: 'No device paired',
  DISABLED_FOR_SCHOOL: 'Device turned off for school restrictions',
  NOT_ENROLLED: 'Paired, setup never finished',
  NEVER_REPORTED: 'Device has never checked in',
  PERMISSION_OFF: 'A permission the blocking needs is off',
  STALE: 'Device has not checked in recently'
};

/** Worst first, so the biggest thing a school can fix is the first thing it reads. */
const COVERAGE_REASON_ORDER: Array<CoverageReason> = [
  'NO_DEVICE',
  'DISABLED_FOR_SCHOOL',
  'NOT_ENROLLED',
  'NEVER_REPORTED',
  'PERMISSION_OFF',
  'STALE'
];

export interface CoverageReasonEntry {
  reason: CoverageReason;
  label: string;
  count: number;
}

/**
 * Whether this restriction was measured at all. A restriction that ran before coverage shipped
 * carries no block, and the page says so instead of showing zeroes that would read as a school
 * with nothing enforcing.
 */
export function hasCoverage(coverage?: CoverageSummaryResponse): boolean {
  return !!coverage && !!coverage.resolvedAt && coverage.targeted > 0;
}

/** The coverage to render, or null when there is none to render. */
export function coverageOf(holder?: { coverage?: CoverageSummaryResponse }): CoverageSummaryResponse | null {
  const coverage = holder?.coverage;
  return hasCoverage(coverage) ? coverage! : null;
}

/** "129 of 251 pupils", the figure the school actually asked for. */
export function coverageHeadline(coverage: CoverageSummaryResponse): string {
  return `${coverage.enforceable} of ${coverage.targeted} pupils could be restricted`;
}

export function coveragePercentage(coverage: CoverageSummaryResponse): number {
  return coverage.targeted > 0 ? (coverage.enforceable / coverage.targeted) * 100 : 0;
}

/** Whole percents, except where rounding would read as a flat zero on a large year group. */
export function percentageLabel(percentage: number): string {
  if (percentage > 0 && percentage < 0.5) {
    return percentage.toFixed(1);
  }
  return Math.round(percentage).toString();
}

/** Red below half the cohort, amber below four fifths, matching the enrolment bars. */
export function coverageTone(coverage: CoverageSummaryResponse): string {
  const percentage = coveragePercentage(coverage);
  if (percentage < 50) {
    return 'is-low';
  }
  return percentage < 80 ? 'is-mid' : 'is-high';
}

/** The reasons that actually apply, worst first. Empty when every pupil could be restricted. */
export function coverageReasons(coverage: CoverageSummaryResponse): Array<CoverageReasonEntry> {
  const counts = coverage.reasons ?? {};
  return COVERAGE_REASON_ORDER
    .map((reason) => ({ reason, label: COVERAGE_REASON_LABELS[reason], count: counts[reason] ?? 0 }))
    .filter((entry) => entry.count > 0);
}

/**
 * Phones that could be restricted at the start and no longer could at the end, among the pupils
 * still targeted at the end. Only worth saying when there were some.
 */
export function coverageDropped(coverage: CoverageSummaryResponse): number | null {
  if (coverage.targetedAtEnd == null || coverage.enforceableAtEnd == null) {
    return null;
  }
  const dropped = coverage.targetedAtEnd - coverage.enforceableAtEnd;
  return dropped > 0 ? dropped : null;
}

/** The pupils on show: all of them, or the ones behind the reason that is being chased. */
export function pupilsForReason(
  pupils: Array<PupilEnforcementResponse>,
  reason?: CoverageReason
): Array<PupilEnforcementResponse> {
  return reason ? pupils.filter((pupil) => pupil.coverageReason === reason) : pupils;
}

/** The three counts a school reads: what the phone confirmed, or that nothing answered at all. */
export type EnforcementBucket = 'SHIELDED' | 'NOT_CONFIRMED' | 'NO_DEVICE';

/**
 * The phone confirmed the shield and did not let go of it before the restriction ended. A phone
 * that released it at the end reads NOT_SHIELDED with both times set, and that is the success
 * case: the same rule the backend counts by.
 */
export function heldTheShield(pupil: PupilEnforcementResponse): boolean {
  if (pupil.state === 'SHIELDED') {
    return true;
  }
  return pupil.state === 'NOT_SHIELDED' && !!pupil.shieldedAt && !!pupil.clearedAt && !pupil.shieldDroppedEarly;
}

/**
 * Which of the three counts a pupil sits in. No state at all means no phone ever answered for
 * them, which is a different problem from a phone that answered without confirming.
 */
export function pupilBucket(pupil: PupilEnforcementResponse): EnforcementBucket {
  if (heldTheShield(pupil)) {
    return 'SHIELDED';
  }
  return pupil.state ? 'NOT_CONFIRMED' : 'NO_DEVICE';
}

/** The pupils behind one of the three counts. */
export function pupilsInBucket(
  pupils: Array<PupilEnforcementResponse>,
  bucket: EnforcementBucket
): Array<PupilEnforcementResponse> {
  return pupils.filter((pupil) => pupilBucket(pupil) === bucket);
}

/**
 * Whether this pupil is one to chase. A phone that confirmed the shield and confirmed the release
 * needs nothing from anyone, so on a year group of hundreds it is hidden until it is asked for.
 */
export function needsAttention(pupil: PupilEnforcementResponse): boolean {
  return !(heldTheShield(pupil) && !pupil.shieldNotCleared);
}

/** The pupils to chase, in the order the backend sent them. */
export function pupilsNeedingAttention(
  pupils: Array<PupilEnforcementResponse>
): Array<PupilEnforcementResponse> {
  return pupils.filter(needsAttention);
}

/** Name search, so one pupil can be found in a list of hundreds. */
export function pupilsMatchingSearch(
  pupils: Array<PupilEnforcementResponse>,
  search: string
): Array<PupilEnforcementResponse> {
  const term = search.trim().toLowerCase();
  if (!term) {
    return pupils;
  }
  return pupils.filter((pupil) => (pupil.childName ?? pupil.childAccountId).toLowerCase().includes(term));
}

/** Why this pupil could not be restricted, or empty when they could have been. */
export function pupilCoverageLabel(pupil: PupilEnforcementResponse): string {
  return pupil.coverageReason ? COVERAGE_REASON_LABELS[pupil.coverageReason] : '';
}

/**
 * What one pupil's phone did, in words. Never "not shielded" for a phone that did not answer:
 * below iOS 16 a device that is shielding correctly cannot answer either way, so silence and a
 * confirmed negative have to read differently.
 */
export function pupilEnforcementLabel(pupil: PupilEnforcementResponse): string {
  if (pupil.state === 'SHIELDED') {
    // The stuck-phone case: the restriction ended and the phone never confirmed the release.
    return pupil.shieldNotCleared ? 'Shielded, release not confirmed by the phone' : 'Shielded';
  }
  if (pupil.shieldDroppedEarly) {
    return 'Phone dropped the restriction before it ended';
  }
  if (heldTheShield(pupil)) {
    return 'Shielded, released when the restriction ended';
  }
  if (pupil.state === 'NOT_SHIELDED') {
    return 'Phone reported no restriction held';
  }
  return pupil.shieldNotConfirmed ? 'Not confirmed by the phone' : 'Waiting for the phone to confirm';
}

export function pupilEnforcementClass(pupil: PupilEnforcementResponse): string {
  return heldTheShield(pupil) && !pupil.shieldNotCleared ? 'pupil-shielded' : 'pupil-unconfirmed';
}
